import React, { useState, useEffect, useCallback } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Alert,
	Chip,
	SelectChangeEvent
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";
import AddAuthorizedUserDialog from "../components/Authorization/AddAuthorizedUserDialog";
import { authorizedUsersService } from "../firebase/authorization/authorizedUsersService";
import { deleteUserFromAuthentication } from "../firebase/authorization/deleteUserFunction";
import { useAuth } from "../context/AuthContext";

interface AuthorizedUser {
	email: string;
	displayName: string;
	role: "admin" | "user";
	createdAt: string;
	isActive: boolean;
	isPartner?: boolean;
}

interface EditingUser extends AuthorizedUser {
	originalEmail: string;
}

const ManageAuthorizedUsers: React.FC = () => {
	const { user, loading: authLoading, userRole } = useAuth();
	const navigate = useNavigate();
	const [users, setUsers] = useState<AuthorizedUser[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>("");
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<AuthorizedUser | null>(
		null
	);
	const [editingUser, setEditingUser] = useState<EditingUser | null>(null);
	const [adminCount, setAdminCount] = useState(0);

	const loadUsers = useCallback(async () => {
		try {
			setLoading(true);
			setError("");
			const activeUsers = await authorizedUsersService.getActiveUsers();
			setUsers(activeUsers);

			// Count admin users
			const adminUsers = activeUsers.filter((u) => u.role === "admin");
			setAdminCount(adminUsers.length);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to load users"
			);
			setUsers([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!authLoading && userRole === "admin") {
			loadUsers();
		}
	}, [authLoading, userRole, loadUsers]);

	const handleAddUserDialogClose = () => {
		setAddDialogOpen(false);
	};

	const handleUserAdded = () => {
		loadUsers(); // Refresh the list
	};

	const handleEditClick = (user: AuthorizedUser) => {
		setEditingUser({
			...user,
			originalEmail: user.email
		});
		setEditDialogOpen(true);
	};

	const handleDeleteClick = (user: AuthorizedUser) => {
		setSelectedUser(user);
		setDeleteDialogOpen(true);
	};

	const canDeleteUser = (
		userToDelete: AuthorizedUser
	): { allowed: boolean; reason?: string } => {
		// Cannot delete yourself
		if (userToDelete.email === user?.email) {
			return {
				allowed: false,
				reason: "You cannot delete your own account"
			};
		}

		// Cannot delete last admin
		if (userToDelete.role === "admin" && adminCount <= 1) {
			return {
				allowed: false,
				reason: "Cannot delete the last admin user. At least one admin must exist."
			};
		}

		return { allowed: true };
	};

	const handleEditSubmit = async () => {
		if (!editingUser) return;

		try {
			setError("");

			// If email changed, remove old and add new
			if (editingUser.email !== editingUser.originalEmail) {
				await authorizedUsersService.removeUser(
					editingUser.originalEmail
				);
				await authorizedUsersService.addUser(
					editingUser.email,
					editingUser.displayName,
					editingUser.role,
					editingUser.isPartner ?? false
				);
			} else {
				// Otherwise update existing user
				const updates: Partial<AuthorizedUser> = {};
				if (
					editingUser.displayName !==
					users.find((u) => u.email === editingUser.originalEmail)
						?.displayName
				) {
					updates.displayName = editingUser.displayName;
				}
				if (
					editingUser.role !==
					users.find((u) => u.email === editingUser.originalEmail)
						?.role
				) {
					updates.role = editingUser.role;
				}
				if (
					(editingUser.isPartner ?? false) !==
					(users.find((u) => u.email === editingUser.originalEmail)
						?.isPartner ?? false)
				) {
					updates.isPartner = editingUser.isPartner ?? false;
				}

				if (Object.keys(updates).length > 0) {
					await authorizedUsersService.updateUser(
						editingUser.originalEmail,
						updates
					);
				}
			}

			setEditDialogOpen(false);
			setEditingUser(null);
			loadUsers();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to update user"
			);
		}
	};

	const handleDeleteConfirm = async () => {
		if (!selectedUser) return;

		const canDelete = canDeleteUser(selectedUser);
		if (!canDelete.allowed) {
			setError(canDelete.reason || "Cannot delete this user");
			setDeleteDialogOpen(false);
			return;
		}

		try {
			setError("");

			// Delete from authorized users database
			await authorizedUsersService.removeUser(selectedUser.email);

			// Also delete from Firebase Authentication
			try {
				const authResult = await deleteUserFromAuthentication(
					selectedUser.email
				);
				if (!authResult.success) {
					console.warn(
						"Warning: User removed from authorized list but may still exist in Authentication",
						authResult.message
					);
				}
			} catch (authError) {
				console.warn(
					"Warning: Could not delete from Authentication:",
					authError
				);
				// Continue anyway - user is already removed from authorized list
			}

			setDeleteDialogOpen(false);
			setSelectedUser(null);
			loadUsers();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to delete user"
			);
			setDeleteDialogOpen(false);
		}
	};

	const handleEditingUserChange = (
		e:
			| React.ChangeEvent<
					HTMLInputElement | { name?: string; value: unknown }
			  >
			| SelectChangeEvent<string>
	) => {
		const { name, value } = e.target;
		setEditingUser((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				[name as string]: value
			};
		});
	};

	const handlePartnerToggle = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setEditingUser((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				isPartner: event.target.checked
			};
		});
	};

	const formatDate = (dateString: string) => {
		try {
			return new Date(dateString).toLocaleDateString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric"
			});
		} catch {
			return dateString;
		}
	};

	// Check if current user is admin
	const isAdmin = userRole === "admin";

	if (authLoading) {
		return (
			<Container maxWidth="lg" sx={{ py: 4 }}>
				<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
					<CircularProgress />
				</Box>
			</Container>
		);
	}

	// If user is not admin, show access denied
	if (!isAdmin) {
		return (
			<Container maxWidth="lg" sx={{ py: 4 }}>
				<Card>
					<CardContent sx={{ textAlign: "center", py: 4 }}>
						<Typography variant="h6" color="error" sx={{ mb: 2 }}>
							Access Denied
						</Typography>
						<Typography color="textSecondary" variant="body1">
							Only administrators can manage authorized users.
						</Typography>
					</CardContent>
				</Card>
			</Container>
		);
	}

	return (
		<Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={3} sx={{ p: 3 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2
					}}
				>
					<Typography variant="h5">
						Manage Authorized Users
					</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Button
							variant="contained"
							startIcon={<HomeIcon />}
							onClick={() => navigate("/")}
						>
							Go to Home
						</Button>
						{isAdmin && (
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={() => setAddDialogOpen(true)}
							>
								Add User
							</Button>
						)}
					</Box>
				</Box>
				<Typography variant="body1" gutterBottom>
					Manage system authorized users with role-based access
					control. Only administrators can add, edit, or remove
					authorized users.
				</Typography>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box
						sx={{ display: "flex", justifyContent: "center", p: 4 }}
					>
						<CircularProgress />
					</Box>
				) : users.length === 0 ? (
					<Card>
						<CardContent sx={{ textAlign: "center", py: 4 }}>
							<Typography color="textSecondary" variant="body1">
								No authorized users found. Click "Add User" to
								get started.
							</Typography>
						</CardContent>
					</Card>
				) : (
					<TableContainer component={Paper}>
						<Table>
							<TableHead
								sx={{
									backgroundColor: (theme) =>
										theme.palette.action.hover,
									color: "text.primary"
								}}
							>
								<TableRow>
									<TableCell
										sx={{
											fontWeight: "bold",
											color: "text.primary"
										}}
									>
										Email
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color: "text.primary"
										}}
									>
										Display Name
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color: "text.primary"
										}}
									>
										Role
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color: "text.primary"
										}}
									>
										Added Date
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color: "text.primary"
										}}
									>
										Status
									</TableCell>
									<TableCell
										sx={{
											fontWeight: "bold",
											color: "text.primary"
										}}
									>
										Actions
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{users.map((userItem) => {
									const isCurrentUser =
										userItem.email === user?.email;
									const canDelete = canDeleteUser(userItem);
									const deleteTooltip = !canDelete.allowed
										? canDelete.reason
										: "Delete user";

									return (
										<TableRow key={userItem.email} hover>
											<TableCell>
												{userItem.email}
												{isCurrentUser && (
													<Chip
														label="You"
														size="small"
														sx={{ ml: 1 }}
													/>
												)}
											</TableCell>
											<TableCell>
												{userItem.displayName}
											</TableCell>
											<TableCell>
												<Chip
													label={
														userItem.role ===
														"admin"
															? "Admin"
															: "User"
													}
													color={
														userItem.role ===
														"admin"
															? "error"
															: "default"
													}
													size="small"
												/>
											</TableCell>
											<TableCell>
												{formatDate(userItem.createdAt)}
											</TableCell>
											<TableCell>
												<Chip
													label={
														userItem.isActive
															? "Active"
															: "Inactive"
													}
													color={
														userItem.isActive
															? "success"
															: "default"
													}
													size="small"
												/>
											</TableCell>
											<TableCell>
												{isAdmin && (
													<>
														<IconButton
															size="small"
															onClick={() =>
																handleEditClick(
																	userItem
																)
															}
															title="Edit user"
															disabled={
																isCurrentUser
															}
														>
															<EditIcon fontSize="small" />
														</IconButton>
														<IconButton
															size="small"
															color="error"
															onClick={() =>
																handleDeleteClick(
																	userItem
																)
															}
															title={
																deleteTooltip
															}
															disabled={
																!canDelete.allowed
															}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</>
												)}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}

				{/* Add User Dialog - Only visible to admins */}
				{isAdmin && (
					<AddAuthorizedUserDialog
						open={addDialogOpen}
						onClose={handleAddUserDialogClose}
						onUserAdded={handleUserAdded}
					/>
				)}

				{/* Edit User Dialog */}
				<Dialog
					open={editDialogOpen}
					onClose={() => setEditDialogOpen(false)}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>Edit Authorized User</DialogTitle>
					<DialogContent>
						{editingUser && (
							<Box
								sx={{
									pt: 2,
									display: "flex",
									flexDirection: "column",
									gap: 2
								}}
							>
								<Typography
									variant="caption"
									color="textSecondary"
								>
									Email (Original: {editingUser.originalEmail}
									)
								</Typography>
								<TextField
									label="Display Name"
									name="displayName"
									value={editingUser.displayName}
									onChange={handleEditingUserChange}
									fullWidth
								/>
								<FormControl fullWidth>
									<InputLabel>Role</InputLabel>
									<Select
										name="role"
										value={editingUser.role}
										onChange={handleEditingUserChange}
										label="Role"
									>
										<MenuItem value="user">User</MenuItem>
										<MenuItem value="admin">Admin</MenuItem>
									</Select>
								</FormControl>
								<FormControlLabel
									control={
										<Checkbox
											checked={
												editingUser.isPartner ?? false
											}
											onChange={handlePartnerToggle}
										/>
									}
									label="Mark as partner"
								/>
							</Box>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setEditDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleEditSubmit}
							variant="contained"
							disabled={!editingUser}
						>
							Save
						</Button>
					</DialogActions>
				</Dialog>

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={deleteDialogOpen}
					onClose={() => setDeleteDialogOpen(false)}
				>
					<DialogTitle>Delete User</DialogTitle>
					<DialogContent>
						{selectedUser && (
							<>
								<Typography>
									Are you sure you want to delete{" "}
									<strong>{selectedUser.email}</strong> (
									{selectedUser.displayName})? This action
									cannot be undone.
								</Typography>
								{selectedUser.role === "admin" &&
									adminCount === 1 && (
										<Alert
											severity="warning"
											sx={{ mt: 2 }}
										>
											This is the last admin user. At
											least one admin must exist in the
											system.
										</Alert>
									)}
								{selectedUser.email === user?.email && (
									<Alert severity="error" sx={{ mt: 2 }}>
										You cannot delete your own account.
									</Alert>
								)}
							</>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDeleteDialogOpen(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleDeleteConfirm}
							color="error"
							variant="contained"
							disabled={
								!selectedUser ||
								!canDeleteUser(selectedUser).allowed
							}
						>
							Delete
						</Button>
					</DialogActions>
				</Dialog>
			</Paper>
		</Container>
	);
};

export default ManageAuthorizedUsers;
